<template>
  <div class="subs-page">
    <div class="subs-container">
      <div class="subs-header">
        <div>
          <p class="subs-kicker">Form submissions</p>
          <h1 class="subs-title">Submissions &amp; export</h1>
          <p class="subs-subtitle">
            Filter submissions, then export exactly what you are looking at as CSV or JSON.
          </p>
        </div>
        <button @click="reload" :disabled="loading" class="subs-btn subs-btn-ghost" data-testid="submissions-refresh">
          Refresh
        </button>
      </div>

      <!-- Filters. The same values drive the listing and the export, which is
           why the export panel below can honestly claim to match them. -->
      <section class="subs-card" data-testid="submissions-filters">
        <h2 class="subs-card-title">Filters</h2>
        <div class="subs-filter-grid">
          <label class="subs-field">
            <span class="subs-label">Form</span>
            <input v-model="filters.formId" data-testid="filter-form" class="subs-input" placeholder="lead" />
          </label>
          <label class="subs-field">
            <span class="subs-label">Page</span>
            <input v-model="filters.pageId" data-testid="filter-page" class="subs-input" placeholder="contact" />
          </label>
          <label class="subs-field">
            <span class="subs-label">Status</span>
            <select v-model="filters.status" data-testid="filter-status" class="subs-input">
              <option value="">Any status</option>
              <option v-for="status in STATUSES" :key="status" :value="status">{{ status }}</option>
            </select>
          </label>
          <label class="subs-field">
            <span class="subs-label">From</span>
            <input v-model="filters.fromDate" type="date" data-testid="filter-from" class="subs-input" />
          </label>
          <label class="subs-field">
            <span class="subs-label">To</span>
            <input v-model="filters.toDate" type="date" data-testid="filter-to" class="subs-input" />
          </label>
        </div>
      </section>

      <!-- AC008: the export panel states the format, the active filters and the
           progress state, so an admin always knows what a download will contain. -->
      <section class="subs-card" data-testid="submissions-export-panel">
        <h2 class="subs-card-title">Export</h2>

        <div class="subs-export-controls">
          <label class="subs-field">
            <span class="subs-label">Format</span>
            <select v-model="exportFormat" data-testid="export-format" class="subs-input">
              <option v-for="format in supportedFormats" :key="format" :value="format">
                {{ format.toUpperCase() }}
              </option>
            </select>
          </label>
          <label class="subs-field">
            <span class="subs-label">Timezone</span>
            <select v-model="exportTimezone" data-testid="export-timezone" class="subs-input">
              <option v-for="zone in TIMEZONES" :key="zone" :value="zone">{{ zone }}</option>
            </select>
          </label>
          <button
            @click="startExport"
            :disabled="exportBusy"
            class="subs-btn subs-btn-primary"
            data-testid="export-start"
          >
            {{ exportBusy ? 'Preparing…' : `Export ${exportFormat.toUpperCase()}` }}
          </button>
        </div>

        <!-- The filter summary the export will apply, echoed from the server so
             it cannot drift from what is actually queried. -->
        <p class="subs-filter-summary" data-testid="export-filter-summary">
          {{ filterSummary }}
        </p>
        <p v-if="exportContext" class="subs-muted" data-testid="export-estimate">
          {{ exportContext.estimatedRowCount }} matching submission(s) ·
          timestamps in {{ exportContext.timezone }} ·
          <span v-if="exportContext.routing === 'async'" data-testid="export-async-notice">
            larger than the {{ exportContext.syncRowLimit }}-row inline limit, so this runs as a background job
          </span>
          <span v-else data-testid="export-sync-notice">downloads immediately</span>
        </p>

        <!-- Async progress. The job reference stays visible until the export
             reaches a terminal state, so a long export is never a blank wait. -->
        <div v-if="exportJob" class="subs-job" data-testid="export-job-status">
          <p class="subs-job-line">
            <span class="subs-job-label">Job</span>
            <code data-testid="export-job-id">{{ exportJob.jobId }}</code>
            <span class="subs-badge" :data-status="exportJob.status" data-testid="export-job-state">
              {{ JOB_LABELS[exportJob.status] || exportJob.status }}
            </span>
          </p>
          <p v-if="exportJob.status === 'succeeded'" class="subs-job-line">
            <a
              :href="exportJob.download.url"
              class="subs-btn subs-btn-primary"
              data-testid="export-job-download"
              download
            >
              Download {{ exportJob.rowCount }} row(s)
            </a>
            <span class="subs-muted">link expires {{ formatStamp(exportJob.download.expiresAt) }}</span>
          </p>
          <p v-else-if="exportJob.status === 'failed'" class="subs-alert" data-testid="export-job-error">
            {{ exportJob.error.message }}
            <span class="subs-muted">({{ exportJob.error.code }}) — {{ exportJob.error.retryGuidance }}</span>
          </p>
        </div>

        <p v-if="exportError" class="subs-alert" data-testid="export-error">
          {{ exportError.message }}
          <span v-if="exportError.code" class="subs-muted">({{ exportError.code }})</span>
        </p>
      </section>

      <!-- The listing the export mirrors. -->
      <section class="subs-card" data-testid="submissions-list">
        <h2 class="subs-card-title">
          Submissions
          <span class="subs-muted" data-testid="submissions-total">{{ pagination.total }} total</span>
        </h2>
        <p v-if="loadError" class="subs-alert" data-testid="submissions-error">{{ loadError }}</p>
        <p v-else-if="loading" class="subs-muted" data-testid="submissions-loading">Loading submissions…</p>
        <p v-else-if="!submissions.length" class="subs-muted" data-testid="submissions-empty">
          No submissions match these filters.
        </p>
        <table v-else class="subs-table">
          <thead>
            <tr>
              <th></th><th>Received</th><th>Form</th><th>Page</th><th>Status</th><th>Fields</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="submission in submissions" :key="submission.id">
              <tr
                class="subs-row"
                :class="{ 'subs-row-open': expandedId === submission.id }"
                :data-testid="`submission-row-${submission.id}`"
                @click="toggleRow(submission)"
              >
                <td class="subs-caret" :aria-expanded="expandedId === submission.id">
                  {{ expandedId === submission.id ? '▾' : '▸' }}
                </td>
                <td>{{ formatStamp(submission.receivedAt) }}</td>
                <td>{{ submission.formIdentifier }}</td>
                <td>{{ submission.pageIdentifier }}</td>
                <td><span class="subs-status" :data-status="submission.status">{{ submission.status }}</span></td>
                <td>{{ submission.payloadSummary.fieldCount }}</td>
              </tr>
              <tr v-if="expandedId === submission.id" class="subs-detail-row" :data-testid="`submission-detail-${submission.id}`">
                <td :colspan="6">
                  <p v-if="detailLoading" class="subs-muted">Loading details…</p>
                  <p v-else-if="detailError" class="subs-alert">{{ detailError }}</p>
                  <dl v-else-if="detail" class="subs-detail">
                    <template v-for="[key, value] in detailEntries" :key="key">
                      <dt>{{ key }}</dt>
                      <dd>{{ formatValue(value) }}</dd>
                    </template>
                    <p v-if="!detailEntries.length" class="subs-muted">No field values on this submission.</p>
                  </dl>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </section>
    </div>
  </div>
</template>

<script setup>
// Submissions & export (Feature BUI-F-18, User Story BUI-US-90 AC008).
//
// One filter state drives three requests — the listing, the export context and
// the export itself — so the screen cannot show one set of rows and hand the
// admin a file containing another.

import { ref, reactive, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import api from '../utils/api'

const STATUSES = ['received', 'processing', 'processed', 'failed', 'spam', 'archived']
const TIMEZONES = ['UTC', 'America/New_York', 'America/Los_Angeles', 'Europe/London', 'Europe/Berlin', 'Asia/Kolkata', 'Asia/Singapore', 'Australia/Sydney']
const JOB_LABELS = {
  queued: 'Queued',
  processing: 'Preparing file…',
  succeeded: 'Ready',
  failed: 'Failed',
}
const POLL_INTERVAL_MS = 2000

const route = useRoute()

const filters = reactive({ formId: '', pageId: '', status: '', fromDate: '', toDate: '' })
const exportFormat = ref('csv')
const exportTimezone = ref('UTC')

const submissions = ref([])
const pagination = ref({ total: 0 })
const loading = ref(false)
const loadError = ref('')

const exportContext = ref(null)
const exportJob = ref(null)
const exportError = ref(null)
const exportBusy = ref(false)
let pollTimer = null

// Row expansion — fetch one submission's full values on demand.
const expandedId = ref(null)
const detail = ref(null)
const detailLoading = ref(false)
const detailError = ref('')
const detailEntries = computed(() => Object.entries(detail.value?.payload || {}))

const formatValue = (v) => {
  if (v === null || v === undefined || v === '') return '—'
  return typeof v === 'object' ? JSON.stringify(v) : String(v)
}

const toggleRow = async (submission) => {
  if (expandedId.value === submission.id) { expandedId.value = null; return }
  expandedId.value = submission.id
  detail.value = null
  detailError.value = ''
  detailLoading.value = true
  try {
    const { data } = await api.get(`/form-submissions/${submission.id}`)
    detail.value = data.submission
  } catch (err) {
    detailError.value = errorFrom(err).message
  } finally {
    detailLoading.value = false
  }
}

const supportedFormats = computed(() => exportContext.value?.supportedFormats || ['csv', 'json'])

// Only non-empty filters are sent, so an untouched field never narrows the
// query — the same convention the listing endpoint documents.
const activeQuery = computed(() => {
  const query = {}
  if (route.params.id) query.siteId = route.params.id
  for (const [key, value] of Object.entries(filters)) {
    if (value !== '' && value !== null && value !== undefined) query[key] = value
  }
  return query
})

const filterSummary = computed(() => {
  const applied = exportContext.value?.filters || activeQuery.value
  const parts = []
  if (applied.siteId) parts.push(`site ${applied.siteId}`)
  if (applied.formId) parts.push(`form "${applied.formId}"`)
  if (applied.pageId) parts.push(`page "${applied.pageId}"`)
  if (applied.status) parts.push(`status ${applied.status}`)
  if (applied.fromDate || applied.toDate) {
    parts.push(`${applied.fromDate || 'the beginning'} → ${applied.toDate || 'now'}`)
  }
  return parts.length
    ? `Exporting ${exportFormat.value.toUpperCase()} for: ${parts.join(' · ')}`
    : `Exporting ${exportFormat.value.toUpperCase()} for: all submissions in this workspace`
})

const formatStamp = (value) => (value ? new Date(value).toLocaleString() : '—')

const errorFrom = (err) => ({
  message: err?.response?.data?.error || err?.message || 'Something went wrong',
  code: err?.response?.data?.code || null,
})

const loadSubmissions = async () => {
  loading.value = true
  loadError.value = ''
  try {
    const { data } = await api.get('/form-submissions', { params: { ...activeQuery.value, limit: 25 } })
    submissions.value = data.submissions || []
    pagination.value = data.pagination || { total: 0 }
  } catch (err) {
    loadError.value = errorFrom(err).message
    submissions.value = []
  } finally {
    loading.value = false
  }
}

// The estimate and routing decision come from the server, not from the row
// count on screen — the visible page is only the first 25 rows.
const loadExportContext = async () => {
  try {
    const { data } = await api.get('/form-submissions/export/context', {
      params: { ...activeQuery.value, format: exportFormat.value, timezone: exportTimezone.value },
    })
    exportContext.value = data
    exportError.value = null
  } catch (err) {
    exportContext.value = null
    exportError.value = errorFrom(err)
  }
}

const reload = async () => {
  await Promise.all([loadSubmissions(), loadExportContext()])
}

const startExport = async () => {
  exportBusy.value = true
  exportError.value = null
  exportJob.value = null
  stopPolling()
  try {
    const response = await api.get('/form-submissions/export', {
      params: { ...activeQuery.value, format: exportFormat.value, timezone: exportTimezone.value },
      responseType: 'blob',
      // 202 is a success here, not an error: the job was accepted.
      validateStatus: (status) => status === 200 || status === 202,
    })

    if (response.status === 202) {
      const body = JSON.parse(await response.data.text())
      exportJob.value = body.job
      startPolling(body.job.jobId)
      return
    }
    downloadBlob(response)
  } catch (err) {
    exportError.value = await blobErrorFrom(err)
  } finally {
    exportBusy.value = false
  }
}

// A blob response type turns error bodies into blobs too; read the JSON back
// out so the admin sees the server's actual reason rather than "[object Blob]".
const blobErrorFrom = async (err) => {
  const payload = err?.response?.data
  if (payload && typeof payload.text === 'function') {
    try {
      const parsed = JSON.parse(await payload.text())
      return { message: parsed.error || 'Export failed', code: parsed.code || null }
    } catch (parseErr) {
      return { message: 'Export failed', code: null }
    }
  }
  return errorFrom(err)
}

const downloadBlob = (response) => {
  const disposition = response.headers?.['content-disposition'] || ''
  const match = disposition.match(/filename="([^"]+)"/)
  const url = URL.createObjectURL(response.data)
  const link = document.createElement('a')
  link.href = url
  link.download = match ? match[1] : `submissions.${exportFormat.value}`
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

const startPolling = (jobId) => {
  pollTimer = setInterval(async () => {
    try {
      const { data } = await api.get(`/form-submissions/export/jobs/${jobId}`)
      exportJob.value = data.job
      if (data.job.status === 'succeeded' || data.job.status === 'failed') stopPolling()
    } catch (err) {
      exportError.value = errorFrom(err)
      stopPolling()
    }
  }, POLL_INTERVAL_MS)
}

const stopPolling = () => {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null }
}

// Any filter or format change invalidates the estimate the panel is showing.
watch([filters, exportFormat, exportTimezone], () => { loadExportContext() }, { deep: true })
watch(filters, () => { loadSubmissions() }, { deep: true })

onMounted(reload)
onUnmounted(stopPolling)
</script>

<style scoped>
.subs-page { min-height: 100%; padding: 2rem 0; }
.subs-container { max-width: 72rem; margin: 0 auto; padding: 0 1.5rem; }
.subs-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; margin-bottom: 1.5rem; }
.subs-kicker { font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.08em; color: var(--text-secondary); }
.subs-title { font-size: 1.75rem; font-weight: 700; color: var(--text-primary); }
.subs-subtitle { color: var(--text-secondary); margin-top: 0.25rem; }

.subs-card {
  background: var(--glass-bg);
  border: var(--glass-border);
  border-radius: var(--radius-md);
  padding: 1.25rem;
  margin-bottom: 1.25rem;
  color: var(--text-primary);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
  box-shadow: var(--elev-sm);
}
.subs-card-title { font-size: 1rem; font-weight: 600; margin-bottom: 0.75rem; color: var(--text-primary); display: flex; justify-content: space-between; align-items: baseline; }

.subs-filter-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr)); gap: 0.75rem; }
.subs-field { display: flex; flex-direction: column; gap: 0.25rem; }
.subs-label { font-size: 0.75rem; color: var(--text-secondary); }

.subs-input {
  width: 100%;
  border: var(--glass-border);
  border-radius: var(--radius-sm);
  padding: 0.5rem 0.625rem;
  font-size: 0.875rem;
  background: var(--glass-bg-active);
  color: var(--text-primary);
  font-family: inherit;
  outline: none;
  color-scheme: dark;
  transition: border 0.15s, background 0.15s, box-shadow 0.15s;
}
.subs-input::placeholder { color: var(--text-muted); }
.subs-input:focus { border: var(--glass-border-hi); background: var(--glass-bg-hover); box-shadow: 0 0 0 3px var(--accent-soft); }

.subs-export-controls { display: flex; flex-wrap: wrap; gap: 0.75rem; align-items: flex-end; }
.subs-btn { border-radius: var(--radius-sm); padding: 0.5rem 0.875rem; font-size: 0.875rem; font-weight: 600; cursor: pointer; border: 1px solid transparent; transition: all 0.15s; }
.subs-btn-primary { background: var(--accent); color: #fff; text-decoration: none; display: inline-block; box-shadow: 0 0 18px var(--accent-glow); }
.subs-btn-primary:hover { background: var(--accent-light); box-shadow: 0 0 26px var(--accent-glow); }
.subs-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; box-shadow: none; }
.subs-btn-ghost { background: transparent; border: var(--glass-border); color: var(--text-primary); }
.subs-btn-ghost:hover { background: var(--glass-bg-hover); }
.subs-btn-ghost:disabled { opacity: 0.6; cursor: not-allowed; }

.subs-filter-summary { margin-top: 0.875rem; font-size: 0.875rem; font-weight: 500; color: var(--text-primary); }
.subs-muted { color: var(--text-secondary); font-size: 0.8125rem; }

.subs-job { margin-top: 1rem; padding-top: 0.875rem; border-top: var(--glass-border); }
.subs-job-line { display: flex; align-items: center; gap: 0.625rem; margin-bottom: 0.5rem; flex-wrap: wrap; font-size: 0.875rem; color: var(--text-primary); }
.subs-job-label { font-size: 0.75rem; text-transform: uppercase; color: var(--text-secondary); }
.subs-job code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; color: var(--text-secondary); }
.subs-badge { border-radius: 999px; padding: 0.125rem 0.625rem; font-size: 0.75rem; background: var(--glass-bg-active); color: var(--text-primary); }
.subs-badge[data-status='succeeded'] { background: rgba(52,211,153,0.18); color: var(--success); }
.subs-badge[data-status='failed'] { background: rgba(248,113,113,0.18); color: var(--danger); }
.subs-alert { color: var(--danger); font-size: 0.875rem; margin-top: 0.75rem; }

.subs-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; color: var(--text-primary); }
.subs-table th, .subs-table td { text-align: left; padding: 0.5rem 0.625rem; border-bottom: var(--glass-border); }
.subs-table th { color: var(--text-secondary); font-weight: 500; font-size: 0.75rem; text-transform: uppercase; }

.subs-row { cursor: pointer; transition: background 0.12s; }
.subs-row:hover { background: var(--glass-bg-hover); }
.subs-row-open { background: var(--glass-bg-active); }
.subs-caret { color: var(--text-secondary); width: 1.5rem; text-align: center; user-select: none; }
.subs-status { font-size: 0.72rem; font-weight: 600; border-radius: 999px; padding: 2px 9px; background: var(--glass-bg-active); color: var(--text-secondary); text-transform: capitalize; }
.subs-status[data-status='received'] { background: rgba(52,211,153,0.18); color: var(--success); }
.subs-status[data-status='processed'] { background: rgba(124,109,250,0.18); color: var(--accent-light); }
.subs-status[data-status='spam'], .subs-status[data-status='failed'] { background: rgba(248,113,113,0.18); color: var(--danger); }

.subs-detail-row > td { background: var(--glass-bg); padding: 0.75rem 1rem 1rem 2rem; }
.subs-detail { display: grid; grid-template-columns: minmax(120px, max-content) 1fr; gap: 6px 18px; margin: 0; }
.subs-detail dt { color: var(--text-secondary); font-size: 0.78rem; font-weight: 600; }
.subs-detail dd { color: var(--text-primary); font-size: 0.85rem; margin: 0; word-break: break-word; }
</style>
